import { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth-context";
import { useSettings } from "@/lib/settings-context";

/**
 * Dismisses the branded application boot loader (rendered in
 * public/index.html so it paints before React mounts) once the app is
 * ACTUALLY ready — never on a fixed timeout.
 *
 * Ready = auth resolved (`!loading`) AND the first settings/branding/
 * dashboard-theme resolution has settled (`initialized`). By that point
 * `applyBranding` + `applyDashboardTheme` have already run, so the theme,
 * branding and dashboard tokens are applied before the loader fades — the
 * user never sees theme flicker, branding swaps, or partial renders.
 */
export default function AppBootGate({ children }) {
    const { loading } = useAuth();
    const { initialized } = useSettings();
    const ready = !loading && initialized;
    const [removed, setRemoved] = useState(false);

    useEffect(() => {
        if (!ready || removed) return;
        const el = document.getElementById("app-boot-loader");
        if (!el) { setRemoved(true); return; }
        // Let the fully-initialised app paint underneath, then fade the
        // overlay out smoothly and remove it from the DOM.
        const raf = requestAnimationFrame(() => el.classList.add("abl-hide"));
        const t = setTimeout(() => { el.remove(); setRemoved(true); }, 520);
        return () => { cancelAnimationFrame(raf); clearTimeout(t); };
    }, [ready, removed]);

    return children;
}
